import { InternalId, StaticReference, Uuid } from '@edgebox/data-definition-kit';
import { Modal } from '@edgebox/react-components';
import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { EmbeddedModal } from '../../../EmbeddedModal';
import { GovernanceTaxonomySelection } from './GovernanceTaxonomySelection';
import { ITaxonomyRecommendations, buildTaxonomySelectionGroups, createTaxonomiesForFacets } from '../taxonomySelection.utils';
import { BP_PORTAL_CLASS_NAME, BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

interface IGovernanceTaxonomyAddModalProps {
  api: ISyncCoreApi;
  show: boolean;
  onHide: () => void;
  existingFacets: TaxonomyFacet[];
  projectUuid?: Uuid;
  /**
   * Byte-sized recommendation payload provided by the host. When absent, the modal
   * renders a plain grouped list without any recommendation info.
   */
  recommendations?: ITaxonomyRecommendations;
  /** Called after taxonomies were created so the caller can refresh its list. */
  onCreated: () => Promise<void> | void;
}

export function GovernanceTaxonomyAddModal({
  api,
  show,
  onHide,
  existingFacets,
  projectUuid,
  recommendations,
  onCreated,
}: IGovernanceTaxonomyAddModalProps) {
  const [selectedFacets, setSelectedFacets] = React.useState<TaxonomyFacet[]>([]);
  const [ignoredFacets, setIgnoredFacets] = React.useState<TaxonomyFacet[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!show) {
      setSelectedFacets([]);
      setIgnoredFacets([]);
      setError(undefined);
    }
  }, [show]);

  const showRecommendationInfo = !!recommendations;

  const groups = React.useMemo(
    () =>
      buildTaxonomySelectionGroups({
        existingFacets,
        aiSuggestions: recommendations?.aiSuggestions || [],
        organizationTypeFacets: recommendations?.organizationTypeFacets || [],
        selectedFacets,
        ignoredFacets,
        showRecommendationInfo,
        // Existing project: let the user add required facets that are still missing.
        allowAddingRequired: true,
      }),
    [existingFacets, ignoredFacets, recommendations, selectedFacets, showRecommendationInfo]
  );

  const acceptFacet = (facet: TaxonomyFacet) => {
    setIgnoredFacets((prev) => prev.filter((candidate) => candidate !== facet));
    setSelectedFacets((prev) => (prev.includes(facet) ? prev : [...prev, facet]));
  };

  const ignoreFacet = (facet: TaxonomyFacet) => {
    setIgnoredFacets((prev) => (prev.includes(facet) ? prev : [...prev, facet]));
  };

  const removeFacet = (facet: TaxonomyFacet) => {
    setSelectedFacets((prev) => prev.filter((candidate) => candidate !== facet));
  };

  const onSubmit = async () => {
    setSaving(true);
    setError(undefined);

    try {
      if (!projectUuid) {
        throw new Error('Project context is missing.');
      }

      const project = await api.service.projects.itemByUuid(projectUuid);
      const projectId = project?.id as InternalId | undefined;
      if (!projectId) {
        throw new Error('Could not resolve the current project.');
      }

      await createTaxonomiesForFacets({
        api,
        facets: selectedFacets,
        projectId,
        customer: project.customer,
        project: new StaticReference(project),
      });

      await onCreated();
      onHide();
    } catch (submitError) {
      setError((submitError as Error)?.message || 'Unable to create taxonomies.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Add taxonomies</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <div className="mb-4 text-sm text-danger">{error}</div> : null}

        <GovernanceTaxonomySelection
          groups={groups}
          onAccept={acceptFacet}
          onIgnore={ignoreFacet}
          onRemove={removeFacet}
          showRecommendationInfo={showRecommendationInfo}
          expandAllCategories
        />
      </Modal.Body>
      <Modal.Footer className="justify-end border-t border-slate-200 pt-6 mt-2">
        <button
          type="button"
          onClick={onHide}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void onSubmit();
          }}
          disabled={saving}
          className={BP_PRIMARY_BUTTON_CLASSES}
        >
          {saving ? 'Adding…' : 'Add taxonomies'}
        </button>
      </Modal.Footer>
    </EmbeddedModal>
  );
}
